import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { getToys, createToy, updateToy, deleteToy, getToySessionsLog, createToySession, extractErrorMessage } from '../services/api';
import { FaPlus, FaTrash, FaEdit, FaPlay } from 'react-icons/fa';
import UserSelect from '../components/UserSelect';
import { useToast } from '../components/Toast';

function Toys({ user }) {
  const toast = useToast();
  const [toys, setToys] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showToyModal, setShowToyModal] = useState(false);
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [editingToy, setEditingToy] = useState(null);
  const [activeTab, setActiveTab] = useState('toys');
  const [savingToy, setSavingToy] = useState(false);
  const [formErrorToy, setFormErrorToy] = useState(null);
  const [savingSession, setSavingSession] = useState(false);
  const [formErrorSession, setFormErrorSession] = useState(null);
  const [newToy, setNewToy] = useState({
    name: '',
    type: 'vibrator',
    description: '',
    image_url: ''
  });
  const [newSession, setNewSession] = useState({
    toy_id: '',
    user_id: '',
    duration: 30,
    intensity: 5,
    notes: ''
  });

  useEffect(() => {
    fetchToys();
    fetchSessions();
  }, []);

  const fetchToys = async () => {
    try {
      setLoading(true);
      const data = await getToys();
      setToys(data.toys || data || []);
    } catch (error) {
      console.error('Error fetching toys:', error);
      toast.error(extractErrorMessage(error, 'Spielzeuge konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  };

  const fetchSessions = async () => {
    try {
      const data = await getToySessionsLog();
      setSessions(data.sessions || data || []);
    } catch (error) {
      console.error('Error fetching toy sessions:', error);
      toast.error(extractErrorMessage(error, 'Sessions konnten nicht geladen werden'));
    }
  };

  const handleCreateToy = async (e) => {
    e.preventDefault();
    if (savingToy) return;
    setSavingToy(true);
    setFormErrorToy(null);
    try {
      if (editingToy) {
        await updateToy(editingToy.id, newToy);
      } else {
        await createToy(newToy);
      }
      setEditingToy(null);
      setNewToy({ name: '', type: 'vibrator', description: '', image_url: '' });
      setShowToyModal(false);
      toast.success(editingToy ? 'Spielzeug aktualisiert' : 'Spielzeug erstellt');
      fetchToys();
    } catch (error) {
      console.error('Error saving toy:', error);
      setFormErrorToy(extractErrorMessage(error, 'Fehler beim Speichern des Spielzeugs'));
    } finally {
      setSavingToy(false);
    }
  };

  const handleCreateSession = async (e) => {
    e.preventDefault();
    if (savingSession) return;
    setSavingSession(true);
    setFormErrorSession(null);
    try {
      await createToySession(newSession);
      setNewSession({ toy_id: '', user_id: '', duration: 30, intensity: 5, notes: '' });
      setShowSessionModal(false);
      toast.success('Session gespeichert');
      fetchSessions();
    } catch (error) {
      console.error('Error creating session:', error);
      setFormErrorSession(extractErrorMessage(error, 'Fehler beim Erstellen der Session'));
    } finally {
      setSavingSession(false);
    }
  };

  const handleDeleteToy = async (id) => {
    if (!window.confirm('Spielzeug wirklich löschen?')) return;
    try {
      await deleteToy(id);
      toast.success('Spielzeug gelöscht');
      fetchToys();
    } catch (error) {
      console.error('Error deleting toy:', error);
      toast.error(extractErrorMessage(error, 'Fehler beim Löschen des Spielzeugs'));
    }
  };

  const closeToyModal = () => {
    if (savingToy) return;
    setShowToyModal(false);
    setFormErrorToy(null);
  };

  const closeSessionModal = () => {
    if (savingSession) return;
    setShowSessionModal(false);
    setFormErrorSession(null);
  };

  const handleEditToy = (toy) => {
    setEditingToy(toy);
    setNewToy({ name: toy.name, type: toy.type, description: toy.description || '', image_url: toy.image_url || '' });
    setShowToyModal(true);
  };

  const toyTypes = ['vibrator', 'plug', 'dildo', 'clamp', 'paddle', 'rope', 'collar', 'cuffs', 'gag', 'blindfold', 'other'];

  return (
    <div className="app-layout">
      <Navbar user={user} />
      <main className="main-content">
        <div className="page-header">
          <h1>Spielzeuge</h1>
          <div className="header-actions">
            <button className="add-btn" onClick={() => { setEditingToy(null); setNewToy({ name: '', type: 'vibrator', description: '', image_url: '' }); setShowToyModal(true); }}>
              <FaPlus /> Neues Spielzeug
            </button>
            <button className="add-btn secondary" onClick={() => setShowSessionModal(true)}>
              <FaPlay /> Session loggen
            </button>
          </div>
        </div>

        <div className="tab-bar">
          <button className={`tab ${activeTab === 'toys' ? 'active' : ''}`} onClick={() => setActiveTab('toys')}>Spielzeuge</button>
          <button className={`tab ${activeTab === 'sessions' ? 'active' : ''}`} onClick={() => setActiveTab('sessions')}>Sessions</button>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : activeTab === 'toys' ? (
          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Typ</th>
                  <th>Beschreibung</th>
                  <th>Bild</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {toys.length > 0 ? toys.map(toy => (
                  <tr key={toy.id}>
                    <td>{toy.name}</td>
                    <td><span className="badge">{toy.type}</span></td>
                    <td>{toy.description || '-'}</td>
                    <td>{toy.image_url ? <img src={toy.image_url} alt={toy.name} className="table-thumb" /> : '-'}</td>
                    <td className="actions-cell">
                      <button className="icon-btn edit" onClick={() => handleEditToy(toy)} title="Bearbeiten"><FaEdit /></button>
                      <button className="icon-btn delete" onClick={() => handleDeleteToy(toy.id)} title="Löschen"><FaTrash /></button>
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan="5" className="no-data">Keine Spielzeuge gefunden</td></tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Datum</th>
                  <th>Spielzeug</th>
                  <th>Dauer (min)</th>
                  <th>Intensität</th>
                  <th>Notizen</th>
                </tr>
              </thead>
              <tbody>
                {sessions.length > 0 ? sessions.map(s => (
                  <tr key={s.id}>
                    <td>{new Date(s.created_at || s.timestamp).toLocaleDateString('de-DE')}</td>
                    <td>{s.toy_name || s.toy_id}</td>
                    <td>{s.duration}</td>
                    <td>
                      <div className="meter-bar inline">
                        <div className="meter-fill" style={{ width: `${(s.intensity / 10) * 100}%` }}></div>
                      </div>
                      <span>{s.intensity}/10</span>
                    </td>
                    <td>{s.notes || '-'}</td>
                  </tr>
                )) : (
                  <tr><td colSpan="5" className="no-data">Keine Sessions gefunden</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {showToyModal && (
          <div className="modal-overlay" onClick={closeToyModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>{editingToy ? 'Spielzeug bearbeiten' : 'Neues Spielzeug'}</h2>
              {formErrorToy && <div className="shared-form-error-banner">{formErrorToy}</div>}
              <form onSubmit={handleCreateToy}>
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={newToy.name}
                    onChange={e => setNewToy({...newToy, name: e.target.value})}
                    placeholder="Name des Spielzeugs"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Typ</label>
                  <select
                    value={newToy.type}
                    onChange={e => setNewToy({...newToy, type: e.target.value})}
                  >
                    {toyTypes.map(t => (
                      <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Beschreibung</label>
                  <textarea
                    value={newToy.description}
                    onChange={e => setNewToy({...newToy, description: e.target.value})}
                    placeholder="Beschreibung..."
                  />
                </div>
                <div className="form-group">
                  <label>Bild URL</label>
                  <input
                    type="url"
                    value={newToy.image_url}
                    onChange={e => setNewToy({...newToy, image_url: e.target.value})}
                    placeholder="https://..."
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeToyModal} disabled={savingToy}>Abbrechen</button>
                  <button type="submit" className="submit-btn" disabled={savingToy}>{savingToy ? 'Speichert…' : (editingToy ? 'Speichern' : 'Erstellen')}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showSessionModal && (
          <div className="modal-overlay" onClick={closeSessionModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>Session loggen</h2>
              {formErrorSession && <div className="shared-form-error-banner">{formErrorSession}</div>}
              <form onSubmit={handleCreateSession}>
                <div className="form-group">
                  <label>Spielzeug *</label>
                  <select
                    value={newSession.toy_id}
                    onChange={e => setNewSession({...newSession, toy_id: e.target.value})}
                    required
                  >
                    <option value="">Spielzeug auswählen...</option>
                    {toys.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <UserSelect
                    value={newSession.user_id}
                    onChange={id => setNewSession({...newSession, user_id: id})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Dauer (Minuten): {newSession.duration}</label>
                  <input
                    type="number"
                    min="1"
                    max="480"
                    value={newSession.duration}
                    onChange={e => setNewSession({...newSession, duration: parseInt(e.target.value) || 0})}
                  />
                </div>
                <div className="form-group">
                  <label>Intensität: {newSession.intensity}/10</label>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={newSession.intensity}
                    onChange={e => setNewSession({...newSession, intensity: parseInt(e.target.value)})}
                    className="slider"
                  />
                </div>
                <div className="form-group">
                  <label>Notizen</label>
                  <textarea
                    value={newSession.notes}
                    onChange={e => setNewSession({...newSession, notes: e.target.value})}
                    placeholder="Notizen zur Session..."
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeSessionModal} disabled={savingSession}>Abbrechen</button>
                  <button type="submit" className="submit-btn" disabled={savingSession}>{savingSession ? 'Speichert…' : 'Session loggen'}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default Toys;
